import React from "react";
import {
  Box,
  Text,
  Icon,
  VStack,
  HStack,
  Button,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { FaFileAlt, FaTrash } from "react-icons/fa";
import { ArtifactProto } from "build-ts/chat_session";
import { NodeInsights } from "containers/Interfaces";
import DataTableComponent from "containers/analysis-plan/DataTableComponent";
import DataFrameDisplayComponent from "components/data_frame/DataFrameDisplayComponent";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import { KGDataFrameProto } from "build-ts/dataframe";
import { FiLayers } from "react-icons/fi";
import { TbLayoutSidebarRightCollapseFilled } from "react-icons/tb";
import { ScheduledJobProto } from "build-ts/schedule";
import ScenarioArtifactCard from "./ScenarioArtifactCard";

interface ArtifactsProps {
  selectedArtifacts?: {
    artifact?: ArtifactProto;
    nodeInsights?: NodeInsights;
    kgDataFrameData?: KGDataFrameProto;
    langgraphMessage?: string[];
    orderIndex: number;
    loading: boolean;
  }[];
  isLoadingArtifact?: boolean;
  onClosePanel?: () => void;
  onClearAll?: () => void;
  onRemoveArtifact?: (artifact: ArtifactProto) => void;
  /** Set of job header ids already persisted (used to show Saved vs Draft badge) */
  savedJobIds?: Set<string>;
  /** Called after a job is manually saved from the card */
  onJobSave?: (savedJob: ScheduledJobProto) => void;
  /** Analyst id passed to ScenarioArtifactCard for saving */
  analystId?: string;
  /** When set, all items except the one with `except` orderIndex will collapse */
  collapseAllExcept?: { except: number; tick: number };
}

const Artifacts: React.FC<ArtifactsProps> = ({
  selectedArtifacts = [],
  isLoadingArtifact = false,
  onClosePanel,
  onClearAll,
  onRemoveArtifact,
  savedJobIds = new Set(),
  onJobSave,
  analystId,
  collapseAllExcept,
}) => {
  const allItems = [...selectedArtifacts].sort((a, b) => b.orderIndex - a.orderIndex);

  const hasAnything = allItems.length > 0 || isLoadingArtifact;

  return (
    <Box height="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">
      {/* ── Header ───────────────────────────────────────── */}
      <HStack
        justifyContent="space-between"
        p={0}
        px={3}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="white"
        flexShrink={0}
        minHeight="fit-content"
      >
        <HStack>
          <Icon as={FiLayers} boxSize={4} color="purple.700" />
          <Text fontSize="xs" fontWeight="700" color="purple.700" m={0}>
            Artifacts
          </Text>
          {allItems.length > 0 && (
            <Button
              leftIcon={<Icon as={FaTrash} boxSize="2.5" />}
              size="xs"
              colorScheme="red"
              color="gray.500"
              _hover={{ color: "red.500", bg: "red.50" }}
              onClick={onClearAll}
              fontSize="xs"
              iconSpacing={1}
              variant="ghost"
              ml={1}
            >
              Clear
            </Button>
          )}
        </HStack>
        <HStack spacing={2}>
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "gray.700", bg: "gray.100" }}
            onClick={onClosePanel}
            p={1}
          >
            <Icon as={TbLayoutSidebarRightCollapseFilled} boxSize="6" />
          </Button>
        </HStack>
      </HStack>

      {/* ── Loading Indicator ─────────────────────────────── */}
      {isLoadingArtifact && (
        <Box
          p={2}
          position="absolute"
          top="15%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="25"
          bg="green.100"
          borderRadius={3}
          boxShadow="md"
        >
          <HStack spacing={2}>
            <Spinner size="sm" color="green.600" thickness="2px" />
            <Text mb={0} fontSize="xs" fontWeight="semibold" color="green.700">
              Loading artifact...
            </Text>
          </HStack>
        </Box>
      )}

      {/* ── Content ───────────────────────────────────────── */}
      <Box
        flex="1"
        overflowY="auto"
        p={0}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "3px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#A0AEC0" },
        }}
      >
        {!hasAnything ? (
          <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={6}>
            <Box textAlign="center">
              <Icon as={FaFileAlt} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.600" fontSize="lg" mb={2}>
                No Artifacts Selected
              </Text>
              <Text fontSize="sm" color="gray.500">
                Click on artifacts in the chat to view them here
              </Text>
            </Box>
          </Box>
        ) : (
          <VStack spacing={1} width="100%" p={1}>
            {allItems.map((item, index) => {
              const isTopItem = index === 0;
              const artifactType = item.artifact
                ? artifactTypeFromJSON(item.artifact.artifact_type)
                : null;

              if (artifactType === ArtifactType.SCENARIO_JOB) {
                const scheduleJob = item.artifact?.scenario_job;
                if (!scheduleJob) return null;
                const jobId = scheduleJob.header?.id ?? "";
                const isSavedInAnalyst = !!jobId && savedJobIds.has(jobId);
                return (
                  <Box key={item.orderIndex} width="100%" overflow="hidden">
                    <ScenarioArtifactCard
                      scheduleJob={scheduleJob}
                      analystId={analystId}
                      isSavedInAnalyst={isSavedInAnalyst}
                      isSelected={isTopItem}
                      defaultExpanded={true}
                      collapseSignal={
                        collapseAllExcept && collapseAllExcept.except !== item.orderIndex
                          ? collapseAllExcept.tick
                          : undefined
                      }
                      expandSignal={
                        collapseAllExcept && collapseAllExcept.except === item.orderIndex
                          ? collapseAllExcept.tick
                          : undefined
                      }
                      onRemove={() => onRemoveArtifact?.(item.artifact!)}
                      onSave={onJobSave}
                    />
                    <Divider my={0} mb={0} mt={1} borderColor="white" />
                  </Box>
                );
              }

              if (artifactType === ArtifactType.KG_OBSERVATION && item.nodeInsights) {
                return (
                  <Box key={item.orderIndex} width="100%" overflow="hidden">
                    <DataTableComponent
                      name={"KG Observation"}
                      observationData={item.nodeInsights}
                      onCloseTable={() => onRemoveArtifact?.(item.artifact!)}
                      isSelected={isTopItem}
                    />
                    <Divider my={0} mb={0} mt={1} borderColor="white" />
                  </Box>
                );
              }

              if (artifactType === ArtifactType.KG_DATAFRAME && item.kgDataFrameData) {
                return (
                  <Box key={item.orderIndex} width="100%" overflow="hidden">
                    <DataFrameDisplayComponent
                      name={item.kgDataFrameData?.header?.name || "KG DataFrame"}
                      data={item.kgDataFrameData}
                      onClose={() => onRemoveArtifact?.(item.artifact!)}
                      isSelected={isTopItem}
                      collapseSignal={
                        collapseAllExcept && collapseAllExcept.except !== item.orderIndex
                          ? collapseAllExcept.tick
                          : undefined
                      }
                    />
                    <Divider my={0} mb={0} mt={1} borderColor="white" />
                  </Box>
                );
              }

              return (
                <Box key={item.orderIndex} p={4}>
                  <Text color="gray.500" fontSize="sm">
                    Unable to display artifact
                  </Text>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default Artifacts;
